import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_DYSLEXIA_SCREENING_ADULT,
  PERSONALITY_TEST_ASSESSMENT_ID,
  traitTitleKeyMap,
} from "../../utils/general-config";

import { getDyslexiaAdultLevelFromScore } from "../../utils/assessment-utils";

import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import {
  getCookie,
  getDyslexiaPageImage,
  getEnneagramBannerImage,
  snakeToTitleCase,
} from "../../utils/utility-functions";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";
import ExpandableSection from "../../components/expandable-collapsible/component";
import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

const DyslexiaScreeningAdultReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const navigate = useNavigate();

  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, isAuth } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const bannerImage = getEnneagramBannerImage();
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);
  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }
        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_DYSLEXIA_SCREENING_ADULT[currentLocale] ||
            MODEL_TYPE_DYSLEXIA_SCREENING_ADULT[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });

        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  const result1ImageText = "dyslexia-report-1";
  const result1Image = getDyslexiaPageImage(result1ImageText);

  const yourScoreTableContent = contentData?.yourScore;
  const interpretationContent = contentData?.interpretation;
  const recommendationContent = contentData?.recommendations;
  const resourcesContent = contentData?.resources;
  const conclusion = contentData?.conclusion;
  const disclaimer = contentData?.disclaimer;

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };

  const progressBarColor = ["#ff6284", "#49A2E6", "#ffce55", "#805bec"];

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        {t("report.adhd.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    if (!title) return null;
    const isString = typeof title === "string";
    let firstWord = "";
    let restOfTitle = "";
    if (isString) {
      const words = title.split(" ");
      firstWord = words[0];
      restOfTitle = words.slice(1).join(" ");
    }

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        <img
          src={bannerImage}
          alt={isString ? title : "Banner"}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        <div
          className={`absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        <div className="relative h-full flex items-start" id={reportType}>
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12">
            <div className="flex flex-col md:flex-row justify-between gap-4 items-start pr-10 md:pr-16">
              <h2
                id={reportType}
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl font-bold text-white text-left break-words whitespace-normal"
              >
                {isString ? (
                  <span className="inline-block max-w-full md:max-w-none leading-tight">
                    <span className="block sm:inline">{firstWord}</span>
                    {restOfTitle && (
                      <>
                        <span className="hidden sm:inline"> </span>
                        <span className="block sm:inline">{restOfTitle}</span>
                      </>
                    )}
                  </span>
                ) : (
                  title
                )}
              </h2>

              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CallToActionSection = ({ onClick1, onClick2 }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.testMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.discoverMessage")}
            </p>
            <TakeTestButton
              onClick={onClick1}
              id="dyslexia-adult-report-take-personality-test-button"
            />
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.bigFiveMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.other_assessment_description")}
            </p>
            <TakeTestButton
              onClick={onClick2}
              id="dyslexia-adult-report-take-big-five-test-button"
            />
          </div>
        </div>
      </section>
    );
  };

  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
      >
        <defs>
          <filter
            id={`shadow-${character || "bullet"}-${actualSize}`}
            x="-30%"
            y="-30%"
            width="160%"
            height="160%"
          >
            <feGaussianBlur
              stdDeviation="3"
              in="SourceAlpha"
              result="blurOut"
            />

            <feOffset dx="0" dy="2" in="blurOut" result="offsetBlur" />

            <feComponentTransfer in="offsetBlur" result="coloredShadow">
              <feFuncA type="linear" slope="0.3" />
            </feComponentTransfer>

            <feMerge>
              <feMergeNode in="coloredShadow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          filter={`url(#shadow-${character || "bullet"}-${actualSize})`}
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };

  const ProgressBar = ({ label, percentage, color }) => {
    return (
      <li>
        <div className="flex justify-between items-center mb-2">
          <span className="font-semibold text-gray-700">{label}</span>
          <span className="text-sm" style={{ color }}>
            {percentage} %
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-5">
          <div
            className="h-5 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          ></div>
        </div>
      </li>
    );
  };

  const RecommendationCard = ({ recommendation }) => {
    // Check if content data exists
    if (!recommendation || !Array.isArray(recommendation?.Content)) {
      return null;
    }

    return (
      <div className="p-4 md:p-6 rounded-2xl shadow-lg bg-purple-100 flex flex-col h-full">
        <div className="flex flex-row gap-2 items-start">
          <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
            <CircleIcon character="i" size={22} />
          </div>
          <h2 className="text-xl font-semibold text-gray-700">
            {recommendation?.RootHeading}
          </h2>
        </div>

        <p
          className="text-gray-700 text-base leading-relaxed mt-6 pl-10"
          style={{ fontStyle: "italic" }}
        >
          Based on your results following steps are suggested:
        </p>

        <div className="space-y-4 mt-4 pl-10">
          {recommendation?.Content?.map((section, index) => {
            // Skip if section is invalid
            if (!section || typeof section !== "object") return null;

            return (
              <div key={index} className="mb-3">
                <h3 className="font-bold text-gray-700 mb-2">
                  {section?.Heading || `Section ${index + 1}`}
                </h3>
                <div className="mt-1 space-y-2">
                  {Array.isArray(section?.Content) &&
                    section?.Content?.map((contentItem, contentIndex) => {
                      // Skip if content item is invalid
                      if (contentItem === null || contentItem === undefined)
                        return null;

                      // Special handling for the Learn More section with bullet points
                      if (section?.Content?.length === 1) {
                        return (
                          <p
                            key={contentIndex}
                            className="text-gray-700 text-base leading-relaxed"
                            style={{ fontStyle: "italic" }}
                          >
                            {contentItem}
                          </p>
                        );
                      } else {
                        return (
                          <div
                            key={contentIndex}
                            className="flex items-start gap-2 pl-1"
                          >
                            <div className="shrink-0 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[2px]">
                              <CircleIcon character="" size={10} />
                            </div>
                            <p className="text-gray-700">{contentItem}</p>
                          </div>
                        );
                      }
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const ResourcesCard = ({ resources }) => {
    // Check if content data exists
    if (!resources || !Array.isArray(resources?.Content)) {
      return null;
    }

    return (
      <div className="p-4 md:p-6 rounded-2xl shadow-lg bg-purple-50 flex flex-col h-full">
        <div className="flex flex-row gap-2 items-start">
          <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
            <CircleIcon character="i" size={22} />
          </div>
          <h2 className="text-xl font-semibold text-gray-700">
            {resources?.RootHeading}
          </h2>
        </div>

        <div className="space-y-4 mt-6 pl-10">
          {resources?.Content?.map((section, index) => {
            // Skip if section is invalid
            if (!section || typeof section !== "object") return null;

            return (
              <div key={index} className="mb-3">
                <h3 className="font-bold text-gray-700 mb-2">
                  {section?.Heading || `Section ${index + 1}`}
                </h3>
                <div className="mt-1 space-y-2">
                  {Array.isArray(section?.Content) &&
                    section?.Content?.map((contentItem, contentIndex) => {
                      // Skip if content item is invalid
                      if (contentItem === null || contentItem === undefined)
                        return null;

                      // Special handling for the Learn More section with bullet points
                      if (section?.Content?.length === 1) {
                        return (
                          <p
                            key={contentIndex}
                            className="text-gray-700 text-base leading-relaxed"
                            style={{ fontStyle: "italic" }}
                          >
                            {contentItem?.Heading}
                          </p>
                        );
                      } else {
                        return (
                          <div
                            key={contentIndex}
                            className="flex items-start gap-2 pl-1"
                          >
                            <div className="shrink-0 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[2px]">
                              <CircleIcon character="" size={10} />
                            </div>
                            <p className="text-gray-700 break-all whitespace-pre-wrap">
                              {contentItem?.Heading}{" "}
                              {contentItem?.Content === "{website}"
                                ? ""
                                : contentItem?.Content}
                            </p>
                          </div>
                        );
                      }
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  function transformUserTraitsData(data) {
    if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

    const result = data.reduce((acc, item) => {
      if (
        item?.trait?.title &&
        typeof item.percentage === "number" &&
        typeof item.score === "number"
      ) {
        acc[item.trait.title] = {
          percentage: item.percentage,
          score: item.score,
        };
      }
      return acc;
    }, {});

    const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

    return { result, totalScore };
  }

  function transformScoreTableContent(
    yourScoreTableContent,
    updatedUserTraits
  ) {
    return {
      ...yourScoreTableContent,
      Content: yourScoreTableContent?.Content?.map((item) => {
        const label = item?.split(":")[0].trim();
        const key = label.toLowerCase().replace(/\s+/g, "_"); // Convert to match updatedUserTraits keys
        return {
          label,
          score: updatedUserTraits?.result[key]?.score || 0,
          percentage: updatedUserTraits?.result[key]?.percentage || 0,
        };
      }),
    };
  }

  function convertToSnakeCaseObject(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return {};

    const toSnakeCase = (str) =>
      str && typeof str === "string"
        ? str
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/_+$/, "")
        : "";

    return arr.reduce((acc, item) => {
      if (!item || typeof item !== "object" || !item.Heading) return acc;

      const key = toSnakeCase(item.EnglishCode.split(":")[0]);
      if (key) acc[key] = item;

      return acc;
    }, {});
  }

  function categorizeUserTraits(interpretationObject, updatedUserTraits) {
    if (!interpretationObject || !updatedUserTraits?.result) return {};

    const getLevel = (score, content) => {
      const level = getDyslexiaAdultLevelFromScore(score); // ⭐ reusable logic

      const matchedItem = (content || []).find((item) =>
        item?.EnglishCode?.startsWith(level)
      );

      return {
        level,
        heading: matchedItem?.Heading?.split(" (")[0] || "",
        details: matchedItem?.Content || "",
      };
    };

    let categorizedResults = {};

    Object.keys(updatedUserTraits.result).forEach((key) => {
      const traitSection = interpretationObject[key];
      if (!traitSection) return;

      const { score } = updatedUserTraits.result[key];
      const { level, heading, details } = getLevel(score, traitSection.Content);

      categorizedResults[key] = {
        Heading: traitSection.Heading,
        score,
        level, // English
        levelLabel: heading, // Spanish label
        details,
      };
    });

    return categorizedResults;
  }

  const updatedUserTraits = transformUserTraitsData(userTraits);

  const interpretationObject = convertToSnakeCaseObject(
    interpretationContent?.Content
  );

  const categorizedData = categorizeUserTraits(
    interpretationObject,
    updatedUserTraits
  );

  const updatedScoreTable = transformScoreTableContent(
    yourScoreTableContent,
    updatedUserTraits
  );

  const progressScore = userTraits?.map((item, index) => {
    const localeKey =
      traitTitleKeyMap[currentLocale] || traitTitleKeyMap[LOCALE.EN];
    return {
      label: snakeToTitleCase(item.trait[localeKey]),
      percentage: item?.percentage,
      color: progressBarColor[index % progressBarColor.length],
    };
  });

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
                      {/* Left Section: Text, Table & Explanation */}
                      <div>
                        <p className="text-gray-700 mb-6">
                          {contentData?.intro}
                        </p>

                        {/* Table Section with better spacing */}
                        <h2 className="text-3xl font-semibold text-gray-700 mt-4">
                          {updatedScoreTable?.RootHeading}
                        </h2>
                        <h2 className="text-2xl font-semibold text-gray-700 my-4">
                          {updatedScoreTable?.FlavorText?.split(":")[0]}
                        </h2>
                        <ul className="space-y-4">
                          {progressScore?.map((item, index) => {
                            return (
                              <ProgressBar
                                color={item.color}
                                label={item?.label}
                                percentage={item?.percentage}
                              />
                            );
                          })}
                        </ul>
                      </div>

                      {/* Right Section: Image */}
                      <div className="flex items-center h-[100%] justify-center">
                        <img
                          src={result1Image}
                          alt="BannerImage"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.interpretation?.RootHeading}
                      color="purple"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 items-stretch">
                      {[
                        {
                          key: "reading_and_writing",
                          bgClr: "bg-yellow-50",
                          textClr: "text-yellow-800",
                        },
                        {
                          key: "memory_and_learning",
                          bgClr: "bg-sky-50",
                          textClr: "text-sky-800",
                        },
                        {
                          key: "phonological_awareness",
                          bgClr: "bg-violet-50",
                          textClr: "text-violet-800",
                        },
                        {
                          key: "attention_and_processing",
                          bgClr: "bg-rose-50",
                          textClr: "text-rose-800",
                        },
                      ].map(({ key, bgClr, textClr }) => (
                        <div key={key} className="flex flex-col h-full">
                          <h2 className="text-lg font-semibold text-gray-700 mt-6 mb-3">
                            {categorizedData?.[key]?.Heading?.split(":")[0]}
                          </h2>

                          <DescriptiveCard
                            heading={categorizedData?.[key]?.levelLabel}
                            overview={categorizedData?.[key]?.details}
                            bgClr={bgClr}
                            textClr={textClr}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={t("report.dyslexia_adult.how_to_use")}
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 items-stretch mb-6">
                      <RecommendationCard
                        recommendation={recommendationContent}
                      />
                      <div className="gap-6 sm:gap-8 flex flex-col">
                        <ResourcesCard resources={resourcesContent} />
                        <DescriptiveCard
                          heading={conclusion?.RootHeading}
                          overview={conclusion?.Content}
                          bgClr={"bg-purple-50"}
                          textClr={"text-purple-800"}
                        />
                      </div>
                    </div>

                    <DescriptiveCard
                      heading={disclaimer?.RootHeading}
                      overview={disclaimer?.Content}
                      bgClr={"bg-emerald-50"}
                      textClr={"text-emerald-800"}
                    />
                  </div>
                </ExpandableSection>
              </section>
              <CallToActionSection
                onClick1={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
                onClick2={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${BIG_FIVE_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
              />
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DyslexiaScreeningAdultReport;
